import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import historyTransaction from "../../media/history_transaction.png";
import { Col, Container, Row, Table } from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import { Button } from "../../components/ui";
import DateInput from "../../components/ui/DateInput";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import filterIcon from "../../media/filter_icon.png";

function TransactionHistory() {
  const { t } = useTranslation("finance");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    clinicId: "all",
    userOwnerId: "all",
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [allClinic, setAllClinic] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isFiltersActive, setIsFiltersActive] = useState(false);

  const loadTransaction = useCallback(async () => {
    const preserveSearchFocus = captureSearchFocus();
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "finance",
        block: "transaction_history",
        status: filters.status === "all" ? "" : filters.status,
        clinicId: filters.clinicId === "all" ? "" : filters.clinicId,
        id_user_owner:
          filters.userOwnerId === "all" ? "" : filters.userOwnerId,
        date_from: dateFrom || "",
        date_to: dateTo || "",
        search: debouncedSearchTerm.trim(),
      });

      setTransactions(data.result || []);
      setAllClinic(data.all_clinic || []);
      setAllUsers(data.all_users || []);
    } catch (err) {
      console.error("Ошибка:", err);
      setError(t("transactions.loadFailed"));
    } finally {
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  }, [
    filters.status,
    filters.clinicId,
    filters.userOwnerId,
    dateFrom,
    dateTo,
    debouncedSearchTerm,
    captureSearchFocus,
    restoreSearchFocus,
    t,
  ]);

  // Загружаем при изменении фильтров
  useEffect(() => {
    document.title = t("transactions.title");
    loadTransaction();
  }, [loadTransaction, t]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  // Есть ли выбранный фильтр: подсвечиваем такие поля, кнопки «Сбросить» больше нет.
  // Диапазон дат считается выбранным, если заполнено хотя бы одно из двух полей.
  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    filters.status !== "all" ||
    filters.clinicId !== "all" ||
    filters.userOwnerId !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    const aTime = Date.parse(a?.date);
    const bTime = Date.parse(b?.date);
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return bTime - aTime;
  });

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={historyTransaction}
                title={t("transactions.title")}
                showStatus={false}
                status={""}
              />
            }
            buttons={[]}
          />
        </Col>
      </Row>
      {/* Основной контент */}
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              {/* Панель поиска */}
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""} ${
                    hasActiveFilters ? "has-active-filters" : ""
                  }`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">
                        {t("filters.title", { ns: "common" })}
                      </div>
                    </div>
                    <Button
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                      size="sm"
                      variant="secondary">
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </Button>
                  </div>
                  <div className="search-section">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("transactions.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`search-input ${searchTerm.trim() ? "is-filter-active" : ""}`}
                    />
                  </div>
                  <div className="filters-section">
                    <div
                      className={`filter-group ${filters.status !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          filters.status === "all" ? "is-placeholder" : ""
                        }
                        value={filters.status}
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("transactions.statusLabel"))}
                        </option>
                        <option value="0">{t("statuses.none")}</option>
                        <option value="1">{t("statuses.pending")}</option>
                        <option value="10">{t("statuses.paid")}</option>
                      </select>
                    </div>
                    <div
                      className={`filter-group ${filters.clinicId !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          filters.clinicId === "all" ? "is-placeholder" : ""
                        }
                        value={filters.clinicId}
                        onChange={(e) =>
                          handleFilterChange("clinicId", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("transactions.clinicLabel"))}
                        </option>
                        {allClinic.map((clinic) => (
                          <option key={clinic.id} value={clinic.id}>
                            {clinic.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`filter-group ${filters.userOwnerId !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          filters.userOwnerId === "all" ? "is-placeholder" : ""
                        }
                        value={filters.userOwnerId}
                        onChange={(e) =>
                          handleFilterChange("userOwnerId", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("transactions.staffLabel"))}
                        </option>
                        {allUsers.map((user) => (
                          <option
                            key={user?.id?.value || user?.id}
                            value={user?.id?.value || user?.id}
                          >
                            {user?.name?.value || user?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`filter-group filter-range ${
                        dateFrom !== "" || dateTo !== "" ? "is-filter-active" : ""
                      }`}
                    >
                      <div className="filter-range-inputs">
                        <DateInput
                          value={dateFrom}
                          hint={asPlaceholder(t("transactions.periodLabel"))}
                          onChange={(e) => setDateFrom(e.target.value)}
                        />
                        <span className="range-separator">—</span>
                        <DateInput
                          value={dateTo}
                          hint={asPlaceholder(t("transactions.periodLabel"))}
                          onChange={(e) => setDateTo(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Список транзакций */}
              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">
                    {t("error", { ns: "common", message: error })}
                  </div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : transactions.length === 0 ? (
                  <div className="no-patients">
                    {t("transactions.notFound")}
                  </div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("transactions.colDate")}</th>
                        <th>{t("transactions.colPatient")}</th>
                        <th>{t("transactions.colClinic")}</th>
                        <th>{t("transactions.colMethod")}</th>
                        <th>{t("transactions.colStatus")}</th>
                        <th>{t("transactions.colAmount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTransactions.map((item) => (
                        <tr key={item?.id?.value || item?.id}>
                          <td>{item?.date || ""}</td>
                          <td>{item?.patient_name || ""}</td>
                          <td className="text-truncate">
                            {item?.name_clinic || ""}
                          </td>
                          <td>{item?.payment_method || ""}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                item?.status === "Оплачено"
                                  ? "status-active"
                                  : item?.status === "Ожидает подтверждения"
                                    ? "status-limited"
                                    : "status-inactive"
                              }`}>
                              {" "}
                              {item?.status}
                            </span>
                          </td>
                          <td>{item?.amount || "—"}</td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={4} className="summary-cell">
                          {t("transactions.found", { count: transactions.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default TransactionHistory;
